<template>
  <validation-observer ref="form" novalidate>
    <div class="row">
      <div class="col-12">
        <section-title
          container-class="tag-badge-main-info mt-4 mb-4"
          content="III. Expectativa y Observaciones Generales"
          icon="fas fa-clipboard-list-check"
        />

        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          rules="required"
        >
          <custom-text-area
            v-model="model.studyGoal"
            :count-classes="['d-block', 'small-counter', 'text-right']"
            custom-error-msg="Campo requerido"
            :disabled="isReadOnly"
            :error="errors[0]"
            label="Meta de estudio, adiestramiento o empleo expresada por el/la estudiante:"
            :maxlength="5000"
            show-char-count
          />
        </validation-provider>
        <div v-else class="d-flex flex-column">
          <label class="text-muted" style="font-size: 14px">
            Meta de estudio, adiestramiento o empleo expresada por el/la
            estudiante:
          </label>
          <span>{{ model.studyGoal }}</span>
        </div>
      </div>
      <div class="col-12">
        <section-title
          container-class="tag-badge-main-info mt-4 mb-4"
          content="IV. Observaciones Generales"
          icon="fas fa-clipboard-list-check"
        />
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          rules="required"
        >
          <custom-text-area
            v-model="model.generalObservations"
            :count-classes="['d-block', 'small-counter', 'text-right']"
            custom-error-msg="Campo requerido"
            :disabled="isReadOnly"
            :error="errors[0]"
            label="Entrevista, Primera Cita, Segunda Cita y Otras Intervenciones:"
            :maxlength="5000"
            show-char-count
          />
        </validation-provider>
        <div v-else class="d-flex flex-column">
          <label class="text-muted" style="font-size: 14px">
            Entrevista, Primera Cita, Segunda Cita y Otras Intervenciones:
          </label>
          <span>{{ model.generalObservations }}</span>
        </div>
      </div>
      <div class="col-12">
        <section-title
          class="mt-3"
          content="Conducta Observada Durante la Evaluación:"
          has-line
        />
        <observed-behavior-selector
          ref="observedBehaviorSelector"
          v-model="model.options"
          :is-read-only="isReadOnly"
          :types="types"
          :validated="formValidated"
        />
      </div>
    </div>
  </validation-observer>
</template>

<script>
//mixins

//utils
import { copy } from '@/utils/objUtils';

//filters

//components
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomTextArea from 'CustomTextArea';
import ObservedBehaviorSelector from './components/ObservedBehaviorSelector.vue';

//services
import { getThreeToFour } from '@/modules/vocational-evaluation/api.js';

//constants
import vocationalEvaluationOptionTypes from '@/utils/constants/vocationalEvaluationOptionTypes';

const observedBehaviorTypes = [
  vocationalEvaluationOptionTypes.AparienciaGeneral,
  vocationalEvaluationOptionTypes.CapacidadParaSeguirInstrucciones,
  vocationalEvaluationOptionTypes.Disposicion,
  vocationalEvaluationOptionTypes.NivelAtencion,
  vocationalEvaluationOptionTypes.NivelActividad,
  vocationalEvaluationOptionTypes.NivelEjecucion,
  vocationalEvaluationOptionTypes.RelacionExaminadora,
  vocationalEvaluationOptionTypes.ConductaObservada,
  vocationalEvaluationOptionTypes.ComunicacionObservada,
  vocationalEvaluationOptionTypes.ToleranciaALasFallas,
  vocationalEvaluationOptionTypes.Lateralidad,
];

export default {
  name: 'VocationalEducationPartThreeToFour',
  components: {
    SectionTitle,
    CustomTextArea,
    ValidationObserver,
    ValidationProvider,
    ObservedBehaviorSelector,
  },
  mixins: [],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    initialModel: {
      type: Object,
      required: false,
      default: () => null,
    },
    keepUpdates: {
      type: Boolean,
      required: false,
    },
    isReadOnly: {
      type: Boolean,
      required: true,
      default: () => false,
    },
  },
  data() {
    return {
      formValidated: false,
      types: observedBehaviorTypes,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    filled() {
      return this.initialModel?.filled ?? false;
    },
  },
  async mounted() {
    this.$emit('update:disabled', true);
    await this.get();
    this.$emit('update:disabled', false);
  },
  beforeDestroy() {
    this.$emit('update:initialModel', this.model);
  },
  methods: {
    setModelFrom(value) {
      const data = copy(value);
      this.model = data;
      this.$emit('update:initialModel', data);
    },
    async get() {
      if (this.keepUpdates && this.filled) {
        this.setModelFrom(this.initialModel);
        return;
      }
      const { data } = await getThreeToFour({
        id: this.id,
        appointmentId: this.appointmentId,
      });
      const result = { ...data, filled: true };
      this.setModelFrom(result);
    },
    async validate() {
      this.formValidated = true;
      const valid = await this.$refs.form.validate();
      const optionsValid = this.$refs.observedBehaviorSelector.validate();
      return optionsValid && valid;
    },
  },
};
</script>
<style scoped></style>
